import { availableNodes } from "../nodeCategoriesConfig";

// Validador estático de flujos (0383 FS-0). Motor puro: sin React, sin red, sin i18n.
// Devuelve códigos + parámetros; la traducción vive en el panel (FlowLintPanel).
//
// Cada regla espeja una condición REAL del motor DB-First (sp_flowbuilder_execute_flow
// y los action-handlers). No se inventan reglas: si el backend no falla por algo, aquí
// como mucho es warning.

export const SEVERITY = { ERROR: "error", WARNING: "warning" };

// El SP anula v_nextNodeId en estos tres tipos → el flujo queda en 'waiting'
// esperando respuesta del usuario. Si no tienen salida, el flujo muere ahí.
const PAUSE_NODES = ["input", "menu", "buttons", "fileInput"];

/**
 * Nodos que TERMINAN el lienzo legítimamente (0419 F1). `end` siempre lo fue; `jumpToFlow`
 * lo es porque entrega el control a otro flujo, y `transferQueue` porque entrega el ticket a
 * una persona: seguir mandando mensajes de bot después de eso es lo que estaría mal. En los
 * tres, quedarse sin salida es su comportamiento correcto, no un cabo suelto.
 *
 * Ojo: `transferQueue` SÍ avanza si le cuelgas algo —el SP resuelve el nodo siguiente—, así
 * que esto solo silencia el aviso de «sin salida», no impide encadenar.
 */
const TERMINAL_NODES = ["end", "jumpToFlow", "transferQueue", "closeTicket"];

// Límite del loop de sp_flowbuilder_execute_flow. Un ciclo sin pausa lo agota
// y el ticket termina en flowStatus='error'.
const MAX_ITERATIONS = 100;

const MEDIA_NODES = ["image", "video", "audio", "document"];

const firstNonEmpty = (...values) =>
  values.find((v) => typeof v === "string" && v.trim() !== "");

// El editor guarda `items` del Bloque de Respuesta como array o como cadena con el array dentro
// (mismo criterio que safeParseItems de ContentNode).
const parseItems = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.trim().startsWith("[")) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

/**
 * Cómo se nombra un nodo en los avisos. En los flujos importados `data.label` es el título del
 * grupo de Typebot («Resuelto», «Group #15»): sin el tipo delante, «el flujo termina en
 * «Resuelto»» no decía que se trata de un Texto dentro de ese grupo.
 */
const nodeLabelOf = (node) => {
  const tipo = availableNodes.find((n) => n.type === node?.type)?.label || node?.type || "";
  const propio = node?.data?.label;
  if (!propio || propio === tipo || propio === node?.type) return tipo;
  return tipo ? `${tipo} · ${propio}` : propio;
};

const OPERADOR_LEGIBLE = {
  equals: "=", notEquals: "≠", contains: "contiene", notContains: "no contiene",
  greaterThan: ">", lessThan: "<", isEmpty: "está vacío", isNotEmpty: "no está vacío",
};

/**
 * Nombre legible de una salida con nombre: las ramas de una condición se guardan con ids internos
 * (los de Typebot, «s43fxtsqv8…»), que en un aviso no dicen nada.
 */
const nombreSalida = (node, handle) => {
  if (handle === "else") return "Si no";
  if (handle === "true") return "Sí";
  if (handle === "false") return "No";
  const rama = (node?.data?.branches || []).find((b) => b && b.id === handle);
  if (rama) {
    const que = (rama.comparisons || [])
      .map((c) => [c.variable, OPERADOR_LEGIBLE[c.operator] || c.operator, c.value].filter((x) => x !== undefined && x !== "").join(" "))
      .join(rama.logic === "OR" ? " o " : " y ");
    return que ? `${rama.label || "Rama"} (${que})` : rama.label || "Rama";
  }
  return handle;
};

const finding = (code, severity, node, params = {}) => ({
  code,
  severity,
  nodeId: node?.id ?? null,
  nodeType: node?.type ?? null,
  nodeLabel: nodeLabelOf(node),
  params,
});

/** Handles de salida esperados por tipo — deben coincidir 1:1 con los Handle del nodo. */
const expectedHandles = (node) => {
  if (node.type === "condition") {
    // Modo ramas (0422 M5): una salida por rama y `else`; si no, el clásico true/false.
    const ramas = Array.isArray(node.data?.branches) ? node.data.branches.filter((b) => b && b.id) : [];
    return ramas.length > 0 ? [...ramas.map((b) => b.id), "else"] : ["true", "false"];
  }
  if (node.type === "menu") {
    const options = node.data?.options || [];
    return options.map(
      (opt, i) => opt.value || opt.text || `option${i + 1}`
    );
  }
  if (node.type === "buttons") {
    const buttons = node.data?.buttons || [];
    return buttons
      .filter((b) => !b.type || b.type === "reply")
      .map((b, i) => b.id || b.text || `btn_${i}`);
  }
  return [];
};

/** FS-0.7 — nodo de inicio: espeja fn_flowbuilder_find_start_node. */
const checkStartNode = (nodes, out) => {
  const starts = nodes.filter((n) => n.type === "start");
  if (starts.length === 0) {
    out.push(finding("NO_START_NODE", SEVERITY.ERROR, null));
    return null;
  }
  if (starts.length > 1) {
    out.push(
      finding("MULTIPLE_START_NODES", SEVERITY.WARNING, starts[1], {
        count: starts.length,
      })
    );
  }
  return starts[0];
};

/** FS-0.3 — nodos inalcanzables desde el start (BFS sobre edges). */
const checkOrphans = (nodes, edges, startNode, out) => {
  if (!startNode) return;
  const adjacency = new Map();
  edges.forEach((e) => {
    if (!adjacency.has(e.source)) adjacency.set(e.source, []);
    adjacency.get(e.source).push(e.target);
  });

  const reachable = new Set([startNode.id]);
  const queue = [startNode.id];
  while (queue.length > 0) {
    const current = queue.shift();
    (adjacency.get(current) || []).forEach((next) => {
      if (!reachable.has(next)) {
        reachable.add(next);
        queue.push(next);
      }
    });
  }

  nodes
    .filter((n) => !reachable.has(n.id))
    .forEach((n) => out.push(finding("ORPHAN_NODE", SEVERITY.WARNING, n)));
};

/** FS-0.2 — salidas: los nodos de pausa sin salida dejan el flujo colgado en 'waiting'. */
const checkExits = (nodes, edges, out) => {
  const outgoing = new Map();
  edges.forEach((e) => {
    if (!outgoing.has(e.source)) outgoing.set(e.source, []);
    outgoing.get(e.source).push(e);
  });

  nodes.forEach((node) => {
    const edgesOut = outgoing.get(node.id) || [];

    if (edgesOut.length === 0) {
      if (PAUSE_NODES.includes(node.type)) {
        out.push(finding("PAUSE_NODE_NO_EXIT", SEVERITY.ERROR, node));
      } else if (node.type === "condition") {
        out.push(
          finding("CONDITION_MISSING_BRANCH", SEVERITY.ERROR, node, {
            missing: expectedHandles(node).map((h) => nombreSalida(node, h)).join(", "),
          })
        );
      } else if (TERMINAL_NODES.includes(node.type)) {
        // Sin salida es lo esperado aquí: no hay nada que avisar.
      } else if (node.type !== "start") {
        out.push(finding("DEAD_END", SEVERITY.WARNING, node));
      } else {
        out.push(finding("START_NO_EXIT", SEVERITY.ERROR, node));
      }
      return;
    }

    // El salto corta el recorrido en el propio SP: lo que cuelgue de él nunca se ejecuta.
    if (node.type === "jumpToFlow") {
      out.push(finding("JUMP_HAS_EXIT", SEVERITY.WARNING, node));
      return;
    }

    // Igual con el cierre (0422 M2): el SP no sigue tras cerrar el ticket.
    if (node.type === "closeTicket") {
      out.push(finding("CLOSE_HAS_EXIT", SEVERITY.WARNING, node));
      return;
    }

    // Tope de intentos (0422): la salida «agotado» no es la normal. Solo «agotado» conectada =
    // una respuesta válida no tiene a dónde ir; con tope y sin «agotado» = sigue preguntando.
    if (node.type === "input" && Number(node.data?.maxAttempts) > 0) {
      const normal = edgesOut.some((e) => e.sourceHandle !== "agotado");
      const agotado = edgesOut.some((e) => e.sourceHandle === "agotado");
      if (!normal) out.push(finding("PAUSE_NODE_NO_EXIT", SEVERITY.ERROR, node));
      if (!agotado) out.push(finding("INPUT_NO_EXHAUSTED_EXIT", SEVERITY.WARNING, node, { count: node.data.maxAttempts }));
      return;
    }

    // Ramas nombradas: el backend rutea por sourceHandle y, si no encuentra, cae a la
    // conexión sin handle. Falta de rama con fallback = warning; sin fallback = error.
    const handles = expectedHandles(node);
    if (handles.length === 0) return;

    const usedHandles = new Set(edgesOut.map((e) => e.sourceHandle || null));
    const hasFallback = usedHandles.has(null);
    const missing = handles.filter((h) => !usedHandles.has(h));
    if (missing.length === 0) return;

    if (node.type === "condition") {
      // Modo ramas (0422 M5): que falte SOLO «Si no» es un diseño legítimo —«si no casa ninguna,
      // aquí se acaba»— y así vienen muchas condiciones de Typebot. Se avisa, no se bloquea.
      const soloElse = missing.length === 1 && missing[0] === "else";
      out.push(
        finding("CONDITION_MISSING_BRANCH", soloElse ? SEVERITY.WARNING : SEVERITY.ERROR, node, {
          missing: missing.map((h) => nombreSalida(node, h)).join(", "),
        })
      );
    } else {
      out.push(
        finding(
          hasFallback ? "BRANCH_USES_FALLBACK" : "BRANCH_NO_EXIT",
          hasFallback ? SEVERITY.WARNING : SEVERITY.ERROR,
          node,
          { missing: missing.join(", "), count: missing.length }
        )
      );
    }
  });
};

/** FS-0.5 — contenido obligatorio, espejo de las validaciones de executeAction. */
const checkContent = (nodes, out, edges = []) => {
  nodes.forEach((node) => {
    const data = node.data || {};

    if (node.type === "text" || node.type === "message") {
      if (!firstNonEmpty(data.text, data.message, data.body)) {
        out.push(finding("EMPTY_TEXT", SEVERITY.ERROR, node));
      }
      return;
    }

    if (MEDIA_NODES.includes(node.type)) {
      if (!firstNonEmpty(data.mediaUrl)) {
        out.push(finding("MISSING_MEDIA", SEVERITY.ERROR, node));
      }
      return;
    }

    if (node.type === "menu") {
      const options = data.options || [];
      if (options.length === 0) {
        out.push(finding("MENU_NO_OPTIONS", SEVERITY.ERROR, node));
      }
      return;
    }

    if (node.type === "buttons") {
      const buttons = data.buttons || [];
      if (buttons.length === 0) {
        out.push(finding("BUTTONS_NO_BUTTONS", SEVERITY.ERROR, node));
      }
      return;
    }

    if (node.type === "hsmTemplate") {
      if (!data.templateId) {
        out.push(finding("HSM_NO_TEMPLATE", SEVERITY.ERROR, node));
      } else if (!data.whatsappId) {
        out.push(finding("HSM_NO_CONNECTION", SEVERITY.WARNING, node));
      }
      return;
    }

    if (node.type === "tag" || node.type === "removeTag") {
      if (!data.tagId) out.push(finding("TAG_NO_TAG", SEVERITY.ERROR, node));
      return;
    }

    if (node.type === "transferQueue") {
      if (!(data.queueId || data.queue)) {
        out.push(finding("TRANSFER_NO_QUEUE", SEVERITY.ERROR, node));
      }
      return;
    }

    if (node.type === "jumpToFlow") {
      if (!(data.flowId || data.targetFlowId)) {
        out.push(finding("JUMP_NO_FLOW", SEVERITY.ERROR, node));
      }
      return;
    }

    // Este nodo envía su PROPIA petición: sin texto, el cliente recibe silencio y el flujo se
    // queda esperando un archivo que nadie le ha pedido.
    if (node.type === "fileInput") {
      // Sin texto propio vale si lo pide el nodo ANTERIOR, que es como lo hace Typebot (bot
      // importado, 0422 T1): un texto justo antes. Solo es error si nadie pide el archivo.
      const loPideAntes = edges.some((e) => {
        if (e.target !== node.id) return false;
        const previo = nodes.find((n) => n.id === e.source);
        return ["text", "message", "content"].includes(previo?.type);
      });
      if (!firstNonEmpty(data.text, data.message) && !loPideAntes) {
        out.push(finding("FILEINPUT_NO_TEXT", SEVERITY.ERROR, node));
      }
      return;
    }

    if (node.type === "setVariable") {
      if (!firstNonEmpty(data.key, data.variable)) {
        out.push(finding("SETVAR_NO_KEY", SEVERITY.ERROR, node));
      }
      return;
    }

    if (node.type === "httpRequest") {
      if (!firstNonEmpty(data.url)) {
        out.push(finding("HTTP_NO_URL", SEVERITY.ERROR, node));
      }
      return;
    }

    if (node.type === "condition") {
      // Modo ramas (0422 M5): la variable va en cada comparación. Una rama sin comparaciones
      // válidas nunca se cumple — su salida es un camino muerto.
      if (Array.isArray(data.branches) && data.branches.length > 0) {
        const vacia = data.branches.some(
          (b) => !(b?.comparisons || []).some((c) => firstNonEmpty(c?.variable))
        );
        if (vacia) out.push(finding("CONDITION_BRANCH_EMPTY", SEVERITY.ERROR, node));
        return;
      }
      if (!firstNonEmpty(data.variable)) {
        out.push(finding("CONDITION_NO_VARIABLE", SEVERITY.ERROR, node));
      }
      return;
    }

    // Bloque de Respuesta: el motor lo expande en una acción por elemento
    // (sp_flowbuilder_process_content_node, doc 0384 C2). Lo que sí se omite en
    // silencio son los elementos sin contenido, así que se avisa de ellos.
    if (node.type === "content") {
      const items = parseItems(data.items);
      if (items.length === 0) {
        out.push(finding("CONTENT_NO_ITEMS", SEVERITY.ERROR, node));
        return;
      }
      const empty = items.filter((item) => {
        const kind = item?.kind || "text";
        if (kind === "text") return !firstNonEmpty(item.text);
        if (kind === "interval") return !(Number(item.seconds) > 0);
        return !firstNonEmpty(item.mediaUrl);
      });
      if (empty.length > 0) {
        out.push(
          finding("CONTENT_ITEMS_EMPTY", SEVERITY.WARNING, node, {
            count: empty.length,
            total: items.length,
          })
        );
      }
      return;
    }

    if (node.type === "openai") {
      if (!data.promptId) {
        out.push(finding("OPENAI_NO_PROMPT", SEVERITY.WARNING, node));
      }
      return;
    }

    if (node.type === "wait" || node.type === "typing") {
      // `seconds` NO es opcional aquí: `sp_flowbuilder_process_wait_node` lee `data.ms` O
      // `data.seconds`, y el editor guarda segundos. Sin esta línea, toda espera configurada
      // en segundos —que el motor respeta— se marcaba como «espera cero» (medido en F2 del 0419).
      const ms = Number(
        data.ms ??
          data.milliseconds ??
          data.delay ??
          data.duration ??
          (data.seconds ? Number(data.seconds) * 1000 : 0)
      );
      if (!ms || ms <= 0) {
        out.push(finding("TIMING_ZERO", SEVERITY.WARNING, node));
      }
    }
  });
};

/** FS-0.4 — ciclos sin nodo de pausa: agotan las 100 iteraciones del SP. */
const checkCycles = (nodes, edges, out) => {
  const adjacency = new Map();
  edges.forEach((e) => {
    if (!adjacency.has(e.source)) adjacency.set(e.source, []);
    adjacency.get(e.source).push(e.target);
  });

  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const state = new Map(); // undefined = sin visitar, 1 = en pila, 2 = cerrado
  const stack = [];
  const reported = new Set();

  const visit = (nodeId) => {
    state.set(nodeId, 1);
    stack.push(nodeId);

    (adjacency.get(nodeId) || []).forEach((next) => {
      if (state.get(next) === 1) {
        const cycleStart = stack.indexOf(next);
        const cycle = stack.slice(cycleStart);
        const hasPause = cycle.some((id) =>
          PAUSE_NODES.includes(nodeById.get(id)?.type)
        );
        const key = [...cycle].sort().join("|");
        if (!hasPause && !reported.has(key)) {
          reported.add(key);
          out.push(
            finding("INFINITE_LOOP", SEVERITY.ERROR, nodeById.get(next), {
              length: cycle.length,
              maxIterations: MAX_ITERATIONS,
            })
          );
        }
      } else if (state.get(next) === undefined) {
        visit(next);
      }
    });

    stack.pop();
    state.set(nodeId, 2);
  };

  nodes.forEach((n) => {
    if (state.get(n.id) === undefined) visit(n.id);
  });
};

/**
 * FS-0.6 — compatibilidad de canal. Reutiliza el flag `wabaOnly` de
 * nodeCategoriesConfig: única fuente de verdad, igual que NodePalette.
 */
const checkProvider = (nodes, provider, out) => {
  const isWaba = String(provider || "").toLowerCase() === "waba";
  if (isWaba) return;

  const wabaOnlyTypes = availableNodes
    .filter((n) => n.wabaOnly)
    .map((n) => n.type);

  nodes
    .filter((n) => wabaOnlyTypes.includes(n.type))
    .forEach((n) =>
      out.push(finding("NODE_REQUIRES_WABA", SEVERITY.ERROR, n))
    );
};

/**
 * Analiza un flujo y devuelve sus hallazgos.
 * @param {{nodes: Array, edges: Array, provider?: string}} flow
 * @returns {{findings: Array, errors: Array, warnings: Array, hasBlockingErrors: boolean}}
 */
export const lintFlow = ({ nodes = [], edges = [], provider = "baileys" } = {}) => {
  const findings = [];

  if (nodes.length === 0) {
    findings.push(finding("EMPTY_FLOW", SEVERITY.ERROR, null));
  } else {
    const startNode = checkStartNode(nodes, findings);
    checkExits(nodes, edges, findings);
    checkContent(nodes, findings, edges);
    checkOrphans(nodes, edges, startNode, findings);
    checkCycles(nodes, edges, findings);
    checkProvider(nodes, provider, findings);
  }

  const errors = findings.filter((f) => f.severity === SEVERITY.ERROR);
  const warnings = findings.filter((f) => f.severity === SEVERITY.WARNING);

  return {
    findings,
    errors,
    warnings,
    hasBlockingErrors: errors.length > 0,
  };
};

export default lintFlow;
